import classNames from 'classnames';
import type { FC } from 'react';
import { memo, useCallback, useMemo } from 'react';

import { useBlockedUsers } from '../../../../../graphdebate/src/hooks/useBlockedUsers';
import { useMe } from '../../../hooks/usePermissions';
import { useAppSelector } from '../../../redux/hooks';
import type { Message, User } from '../../../types/api';
import { AuthorAvatar } from '../../AuthorAvatar';
import { newResponseKeyInBbf } from '../thread-service';
import type { MessageId } from '../thread-slice';

export type ClickBubbleFlowHandler = (
  itemId: string | null,
  options?: { offset?: number; fromScroll?: boolean },
) => void;

interface Props {
  messageId: MessageId;
  index: number;
  isSelectedMessage: boolean;
  onClick: ClickBubbleFlowHandler;
}

export const BubbleFlowItem: FC<Props> = memo(function BubbleFlowItem(props) {
  const { messageId, index, onClick, isSelectedMessage } = props;

  const selectItem = useCallback(() => {
    if (messageId) {
      onClick?.(messageId);
    }
  }, [messageId, onClick]);

  // Padding and border are applied to a separate div, so that the zoomed aread doesn't include them.
  return (
    <div
      role="button"
      onClick={selectItem}
      tabIndex={0}
      className={classNames(
        'pb-[6px] border-b-2 mx-[1px] flex flex-col items-center h-[80px] w-[40px] justify-center',
        isSelectedMessage ? 'border-primary' : 'border-transparent',
      )}
    >
      {/* The fixed height h-5 is important to align with all other bubbles and arrows. */}
      {isSelectedMessage && <div className="h-5 text-xs"># {index + 1}</div>}
      <div className={classNames('flex items-center h-10', !isSelectedMessage && 'mt-5')}>
        <div
          className={classNames(
            'flex flex-col items-center justify-center cursor-pointer transition-all ease-in-out duration-200',
            isSelectedMessage ? 'h-10 w-10' : 'h-6 w-6',
            !isSelectedMessage &&
              'grayscale hover:grayscale-0 hover:drop-shadow-[0px_2px_6px_#00000029] hover:h-[38px] hover:w-[38px]',
          )}
        >
          {messageId && (
            <BubbleFlowAvatar messageId={messageId} isSelectedMessage={isSelectedMessage} />
          )}
        </div>
      </div>
    </div>
  );
});

type BubbleFlowAvatarProps = Pick<Props, 'isSelectedMessage'> & {
  messageId: MessageId;
};

const BubbleFlowAvatar: FC<BubbleFlowAvatarProps> = memo(function BubbleFlowAvatar(props) {
  const { messageId, isSelectedMessage } = props;
  const { isBlocked, isNative } = useBlockedUsers();
  const me = useMe();
  const newRespMsg = useMemo(() => {
    const msg: Message = { id: newResponseKeyInBbf, author: me };
    return msg;
  }, [me]);
  const message = useAppSelector(state =>
    messageId === newResponseKeyInBbf ? newRespMsg : state.yakli.messages?.[messageId],
  );
  const isAuthorBlocked = isNative && isBlocked((message?.author as User)?.id);

  if (!message) {
    return null;
  }

  return (
    <AuthorAvatar
      author={message.author}
      userId={(message.author as User).id}
      profile={(message.author as User).profile}
      // fullSize
      size={isSelectedMessage ? 'medium2' : 'small2'}
      isUserBlocked={isAuthorBlocked}
    />
  );
});
